import { memo, useState, useCallback, useEffect, useRef, Fragment } from 'react';
import { Markdown } from './Markdown';
import type { JSONValue, UIMessage } from 'ai';
import { assistantMessagePropsEqual } from './assistant-message-memo';
import Popover from '~/components/ui/Popover';
import { workbenchStore } from '~/lib/stores/workbench';
import { WORK_DIR } from '~/utils/constants';
import WithTooltip from '~/components/ui/Tooltip';
import { ConfirmationDialog } from '~/components/ui/Dialog';
import type { ProviderInfo } from '~/types/model';
import { AskUserQuestions } from './AskUserQuestions';
import { SubAgentProgress } from './SubAgentProgress';
import { TestResults } from './TestResults';
import { buildAskUserResponseMessage } from './ask-user-response';
import { answerAskUser } from '~/lib/api/chats';
import { chatId as chatIdStore } from '~/lib/persistence/useChatHistory';
import { initSubAgentLog, appendSubAgentStep, appendSubAgentText, completeSubAgentLog } from '~/lib/stores/sub-agent';
import type {
  AskUserQuestion,
  SubAgentState,
  TestResultsState,
  TestSuite,
  TestResultsSummary,
  SubAgentToolAnnotation,
} from '~/types/context';
import { buildOrderedSegments, type RenderSegment, type ToolPart } from './message-parts';
import { extractMemorySignals, recallSummary, type MemoryRecallAnnotation } from './memory-annotations';
import { updateStreamingFile, setEditingFile } from '~/lib/stores/streaming-file';
import { activeEditedFilePath } from './active-edit';
import { foldSegments, foldTurn, type Block } from './fold-segments';
import { ToolLine } from './tools/ToolLine';
import { ToolGroup } from './tools/ToolGroup';
import { WorkedBlock, narrationLine } from './tools/WorkedBlock';
import { DuneaSpinner } from './tools/DuneaSpinner';
import { SubAgentGroup } from './tools/SubAgentGroup';
import { normalizedFilePath, openFileInWorkbench } from './tools/file-link';

interface AssistantMessageProps {
  content: string;
  /** Ordered AI-SDK message parts — rendered in chronological order so text,
   *  reasoning, and tool calls keep their original interleaving. */
  parts?: any[];
  annotations?: JSONValue[];
  metadata?: Record<string, any>;
  messageId?: string;
  isLastMessage?: boolean;
  /** True while the chat is actively generating — distinguishes a live write from a stale/interrupted one. */
  isStreaming?: boolean;
  /** True when a checkpoint exists for this message — gates the revert button. */
  hasCheckpoint?: boolean;
  /** Suppress the "Using memories" chip when an earlier turn already showed the
   *  same recall payload — keeps the timeline from repeating it every turn. */
  hideMemoryRecall?: boolean;
  onRestore?: (messageId: string) => void;
  onFork?: (messageId: string) => void;
  append?: (message: UIMessage) => void;
  chatMode?: 'discuss' | 'build';
  setChatMode?: (mode: 'discuss' | 'build') => void;
  model?: string;
  provider?: ProviderInfo;
}

type AskUserResponse = {
  questionId: string;
  question: string;
  answer: string;
};

export const AssistantMessage = memo(
  ({
    content,
    parts,
    annotations,
    metadata,
    messageId,
    isLastMessage = false,
    isStreaming = false,
    hasCheckpoint = false,
    hideMemoryRecall = false,
    onRestore,
    onFork,
    append,
    chatMode,
    setChatMode,
    model,
    provider,
  }: AssistantMessageProps) => {
    const [askUserSubmitted, setAskUserSubmitted] = useState(false);
    // Synchronous re-entry guard: `askUserSubmitted` is async state, so a fast
    // second click / double-invoked handler could `append()` the answer twice
    // before it flips — that double-fires the turn start, the AI rejects the
    // duplicate (CHAT_TURN_IN_PROGRESS), and the UI latches onto the rejected
    // turn showing a false DONE with an empty preview while the real turn builds
    // in the background (chat 662453a5). This ref blocks the second dispatch
    // synchronously, before any re-render.
    const askUserDispatchedRef = useRef(false);
    const [copied, setCopied] = useState(false);

    const filteredAnnotations = (annotations?.filter(
      (annotation: JSONValue) =>
        annotation && typeof annotation === 'object' && Object.keys(annotation).includes('type'),
    ) || []) as { type: string; value: any } & { [key: string]: any }[];

    let chatSummary: string | undefined = undefined;

    if (filteredAnnotations.find((annotation) => annotation.type === 'chatSummary')) {
      chatSummary = filteredAnnotations.find((annotation) => annotation.type === 'chatSummary')?.summary;
    }

    let codeContext: string[] | undefined = undefined;

    if (filteredAnnotations.find((annotation) => annotation.type === 'codeContext')) {
      codeContext = filteredAnnotations.find((annotation) => annotation.type === 'codeContext')?.files;
    }

    const usage: {
      completionTokens: number;
      promptTokens: number;
      totalTokens: number;
    } =
      (metadata?.type === 'usage' ? metadata.value : undefined) ??
      filteredAnnotations.find((annotation) => annotation.type === 'usage')?.value;

    // Detect ask_user — check metadata first (AI SDK v6), fall back to annotations (legacy)
    const askUserAnnotation =
      (metadata?.askUser as
        | { type: 'askUser'; questions: AskUserQuestion[]; sessionKey?: string; answers?: unknown[] }
        | undefined) ??
      (filteredAnnotations.find((annotation) => annotation.type === 'askUser') as
        | { type: 'askUser'; questions: AskUserQuestion[]; sessionKey?: string; answers?: unknown[] }
        | undefined);
    const hasAskUserQuestions = !!askUserAnnotation?.questions?.length;

    // Render parts in their ORIGINAL chronological order (text ↔ reasoning ↔
    // tool calls interleaved), not grouped by type. See message-parts.ts.
    const segments = buildOrderedSegments(parts);

    const isToolVisible = (part: ToolPart) => {
      // Hide task_manager — progress is shown in the sticky bottom bar
      if (part.type === 'tool-task_manager') {
        return false;
      }

      if (part.type !== 'tool-ask_user') {
        return true;
      }

      const askUserStatus =
        part.output && typeof part.output === 'object' ? (part.output as { status?: string }).status : undefined;

      return !hasAskUserQuestions && askUserStatus !== 'blocked';
    };

    const visibleSegments = segments.filter((s) => (s.kind === 'tool' ? isToolVisible(s.part) : true));
    const hasReasoning = segments.some((s) => s.kind === 'reasoning');

    // Detect sub-agent annotations and compute state
    const subAgentAnnotations = filteredAnnotations.filter(
      (a: any) => a.type === 'subAgentSpawn' || a.type === 'subAgentProgress' || a.type === 'subAgentComplete',
    ) as any[];

    const subAgentState: SubAgentState | undefined = (() => {
      if (subAgentAnnotations.length === 0) {
        return undefined;
      }

      const spawnAnnotation = subAgentAnnotations.find((a) => a.type === 'subAgentSpawn');

      if (!spawnAnnotation) {
        return undefined;
      }

      const agents = spawnAnnotation.agents.map((a: any) => ({
        ...a,
        status: 'pending' as const,
      }));

      // Apply progress updates
      for (const ann of subAgentAnnotations) {
        if (ann.type === 'subAgentProgress') {
          const agent = agents.find((a: any) => a.id === ann.agentId);

          if (agent) {
            agent.status = ann.status;
            agent.toolName = ann.toolName;
          }
        }
      }

      // Apply completion
      const completeAnnotation = subAgentAnnotations.find((a) => a.type === 'subAgentComplete');

      if (completeAnnotation) {
        for (const result of completeAnnotation.agents) {
          const agent = agents.find((a: any) => a.id === result.id);

          if (agent) {
            agent.status = result.success ? 'complete' : 'error';
            agent.success = result.success;
          }
        }
      }

      return {
        agents,
        isComplete: !!completeAnnotation,
      };
    })();

    // Detect sub-agent tool streaming annotations and sync to store
    const subAgentToolAnnotations = filteredAnnotations.filter(
      (a: any) => typeof a.type === 'string' && a.type.startsWith('subAgentTool'),
    ) as SubAgentToolAnnotation[];

    // Build a map of toolCallId → latest steps + streamed text for inline display
    const subAgentToolLogs = new Map<
      string,
      { label: string; steps: string[]; streamedText: string; status: string; summary?: string }
    >();

    for (const ann of subAgentToolAnnotations) {
      if (ann.type === 'subAgentToolStart') {
        subAgentToolLogs.set(ann.toolCallId, {
          label: ann.label,
          steps: [],
          streamedText: '',
          status: 'running',
        });
      } else if (ann.type === 'subAgentToolStep') {
        const log = subAgentToolLogs.get(ann.toolCallId);

        if (log) {
          log.steps.push(ann.message);
        }
      } else if (ann.type === 'subAgentToolText') {
        const log = subAgentToolLogs.get(ann.toolCallId);

        if (log) {
          log.streamedText += ann.delta;
        }
      } else if (ann.type === 'subAgentToolComplete') {
        const log = subAgentToolLogs.get(ann.toolCallId);

        if (log) {
          log.status = ann.error ? 'error' : 'complete';
          log.summary = (ann as any).summary;
        }
      }
    }

    // Logs with NO matching tool part are server-side pre-passes (e.g. the
    // clone source capture, which runs BEFORE the model's first tool call).
    // OrderedSegments only renders logs attached to a tool part, so without
    // this they'd be invisible and the user would stare at "Generating…"
    // through a 40s capture.
    const toolPartIds = new Set<string>();

    for (const p of (parts ?? []) as any[]) {
      if (typeof p?.type === 'string' && p.type.startsWith('tool-') && p.toolCallId) {
        toolPartIds.add(p.toolCallId);
      }

      const outId = p?.output?.toolCallId;

      if (outId) {
        toolPartIds.add(outId);
      }
    }

    // The pre-pass panel is LIVE narration only: visible while the capture is
    // running, gone the moment it completes (success or failure) — the build
    // output speaks for itself and a finished capture log is just noise.
    const orphanAgentLogs = [...subAgentToolLogs.entries()].filter(
      ([id, log]) => !toolPartIds.has(id) && log.status === 'running',
    );

    // Sync to nanostore for the workbench panel. Track the last processed index
    // so we only forward NEW annotations on each render — without this, every
    // re-render would re-append every prior step/delta, blowing up text streams.
    const subAgentProcessedRef = useRef(0);
    useEffect(() => {
      for (let i = subAgentProcessedRef.current; i < subAgentToolAnnotations.length; i++) {
        const ann = subAgentToolAnnotations[i];

        if (ann.type === 'subAgentToolStart') {
          initSubAgentLog(ann.toolCallId, ann.agentId, ann.label);
        } else if (ann.type === 'subAgentToolStep') {
          appendSubAgentStep(ann.toolCallId, {
            timestamp: Date.now(),
            toolName: ann.toolName,
            message: ann.message,
            status: ann.status,
          });
        } else if (ann.type === 'subAgentToolText') {
          appendSubAgentText(ann.toolCallId, ann.delta);
        } else if (ann.type === 'subAgentToolComplete') {
          completeSubAgentLog(ann.toolCallId, ann.summary, ann.error);
        }
      }

      subAgentProcessedRef.current = subAgentToolAnnotations.length;
    }, [subAgentToolAnnotations.length]);

    // Live file streaming → workbench code viewer. While the model writes a file,
    // its `write_file` tool input arrives incrementally (state 'input-streaming'
    // with a growing `input.content`, then 'input-available' once fully buffered).
    // Feed the in-flight file into the streaming store so the code viewer can type
    // it out, and auto-open the panel once per file. Only the last message drives
    // this — older messages re-rendering must not hijack the panel.
    const writeFile = (() => {
      if (!isLastMessage) {
        return null;
      }

      const writes = (parts ?? []).filter((p: any) => p?.type === 'tool-write_file' && p?.toolCallId);

      if (writes.length === 0) {
        return null;
      }

      // The actively-streaming write, or the most recent one if all have finished.
      const active =
        writes.find((p: any) => p.state === 'input-streaming' || p.state === 'input-available') ??
        writes[writes.length - 1];
      const filePath = active?.input?.filePath;

      if (!filePath) {
        // `filePath` legitimately hasn't streamed in yet — not an error, just wait.
        return null;
      }

      const fullPath = `${WORK_DIR}/${String(filePath).replace(/^\//, '')}`;
      const content = typeof active.input?.content === 'string' ? active.input.content : '';
      // Distinguish a successful write from a failed one — a failed write must not
      // masquerade as a completed file in the viewer.
      const phase =
        active.state === 'output-error' ? 'error' : active.state === 'output-available' ? 'done' : 'streaming';
      const errorText = active.state === 'output-error' ? active.errorText : undefined;

      return { fullPath, content, phase, errorText };
    })();

    const openedFileRef = useRef<string | null>(null);
    useEffect(() => {
      if (!writeFile) {
        return;
      }

      // Authoritative status from (terminal state, is-the-chat-generating):
      //  - output-error  → error
      //  - output-available → done
      //  - no terminal state + chat generating → streaming (live; re-arms even if a
      //    resumable stream briefly looked finished on reload)
      //  - no terminal state + chat NOT generating → done (interrupted/hung run or a
      //    reloaded half-finished message — stop the "writing" spinner, don't perpetuate it)
      const status =
        writeFile.phase === 'error'
          ? 'error'
          : writeFile.phase === 'done'
            ? 'done'
            : isStreaming
              ? 'streaming'
              : 'done';

      updateStreamingFile(
        writeFile.fullPath,
        writeFile.content,
        status,
        status === 'error' ? writeFile.errorText : undefined,
      );

      // Track the file being written so the editor shows it IF the user opens the
      // Code view — but never force them there. Writing a file used to hijack the
      // panel (switch to Code + open the workbench), yanking the user off the
      // Preview they were watching. Let the agent write; the user goes to Code
      // only if they choose to.
      if (isStreaming && openedFileRef.current !== writeFile.fullPath) {
        openedFileRef.current = writeFile.fullPath;
        workbenchStore.setSelectedFile(writeFile.fullPath);
      }
    }, [writeFile?.fullPath, writeFile?.content, writeFile?.phase, writeFile?.errorText, isStreaming]);

    // "Editing <file>" indicator for edit_file — diff-shaped input, nothing to
    // stream, but the user must see which file is being modified. Last message
    // only, and only while actually generating (mirrors the write_file effect).
    const editedPath = isLastMessage && isStreaming ? activeEditedFilePath(parts) : null;
    useEffect(() => {
      const fullPath = editedPath ? `${WORK_DIR}/${String(editedPath).replace(/^\//, '')}` : null;
      setEditingFile(fullPath);

      // Same as the write_file effect — track the edited file without forcing the
      // user over to the Code view. They open Code themselves if they want it.
      if (fullPath && openedFileRef.current !== fullPath) {
        openedFileRef.current = fullPath;
        workbenchStore.setSelectedFile(fullPath);
      }
    }, [editedPath]);

    // Detect test results annotations and compute state
    const testResultsAnnotations = filteredAnnotations.filter(
      (a: any) =>
        typeof a.type === 'string' &&
        (a.type === 'testResultsStart' || a.type === 'testSuiteResult' || a.type === 'testResultsComplete'),
    ) as any[];

    const testResultsState: TestResultsState | undefined = (() => {
      if (testResultsAnnotations.length === 0) {
        return undefined;
      }

      const suites: TestSuite[] = [];
      let isRunning = true;
      let completeSummary: TestResultsSummary | undefined;

      for (const ann of testResultsAnnotations) {
        if (ann.type === 'testSuiteResult') {
          const existing = suites.find((s) => s.id === ann.suiteId);

          if (existing) {
            existing.status = ann.status;
            existing.tests = ann.tests;
            existing.duration = ann.duration;
          } else {
            suites.push({
              id: ann.suiteId,
              name: ann.suiteName,
              status: ann.status,
              tests: ann.tests || [],
              duration: ann.duration,
            });
          }
        } else if (ann.type === 'testResultsComplete') {
          isRunning = false;
          completeSummary = ann.summary;
        }
      }

      const allTests = suites.flatMap((s) => s.tests);
      const summary: TestResultsSummary = completeSummary ?? {
        passed: allTests.filter((t) => t.status === 'passed').length,
        failed: allTests.filter((t) => t.status === 'failed').length,
        skipped: allTests.filter((t) => t.status === 'skipped').length,
        total: allTests.length,
        duration: suites.reduce((acc, s) => acc + (s.duration ?? 0), 0),
      };

      return { suites, summary, isRunning };
    })();

    // Agent-memory signals (data-memory parts): recalled context + saved facts.
    const memorySignals = extractMemorySignals(filteredAnnotations);

    // Already answered when: the user replied in a later message (classic
    // path), or the turn waited and took the answers in-turn (the server
    // stamps them onto the card's metadata so a reload doesn't re-open it).
    const isAskUserSubmitted = askUserSubmitted || !isLastMessage || !!askUserAnnotation?.answers?.length;

    const handleAskUserSubmit = useCallback(
      (answers: { questionId: string; answer: string }[]) => {
        if (!append || !askUserAnnotation || askUserDispatchedRef.current) {
          return;
        }

        askUserDispatchedRef.current = true;
        setAskUserSubmitted(true);

        const structuredAnswers: AskUserResponse[] = answers.map((a) => {
          const q = askUserAnnotation.questions.find((q) => q.id === a.questionId);

          return {
            questionId: a.questionId,
            question: q?.question ?? a.questionId,
            answer: a.answer,
          };
        });

        const sendAsNewMessage = () =>
          append(
            buildAskUserResponseMessage({
              answers: structuredAnswers,
              sessionKey: askUserAnnotation.sessionKey,
              model,
              provider,
            }) as any,
          );

        // While the generation is still live the turn is parked on this
        // question: hand the answers straight to it and the build carries on
        // in the same turn. If nothing is waiting (the wait timed out, or the
        // turn already ended) fall back to the classic new-message path.
        const currentChatId = chatIdStore.get();

        if (!isStreaming || !currentChatId) {
          sendAsNewMessage();

          return;
        }

        answerAskUser(currentChatId, structuredAnswers, askUserAnnotation.sessionKey)
          .then(({ delivered }) => {
            if (!delivered) {
              sendAsNewMessage();
            }
          })
          .catch(() => sendAsNewMessage());
      },
      [append, askUserAnnotation, model, provider, isStreaming],
    );

    const handleCopyContent = () => {
      navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };

    // Hide entirely empty messages (e.g. task_manager-only calls with no text)
    const hasVisibleTools = visibleSegments.some((s) => s.kind === 'tool');
    const hasVisibleContent =
      content ||
      hasReasoning ||
      hasVisibleTools ||
      hasAskUserQuestions ||
      usage ||
      subAgentState ||
      testResultsState ||
      // saved-memory chips are no longer rendered, so a saved signal alone
      // doesn't make a message visible — only a recall card does.
      memorySignals.recall;

    if (!hasVisibleContent) {
      return null;
    }

    // A server-appended restore-checkpoint marker — render as a compact card
    // instead of a normal assistant bubble, with only its own revert button
    // (no copy/fork).
    if (metadata?.type === 'restore') {
      return (
        <RestoreCard
          content={content}
          messageId={messageId}
          hasCheckpoint={hasCheckpoint}
          isStreaming={isStreaming}
          onRestore={onRestore}
        />
      );
    }

    return (
      <div className="flex gap-3 w-full group/msg">
        {/* Avatar hidden on phones — its 40px is better spent on the message. */}
        <div className="hidden sm:flex flex-shrink-0 w-7 h-7 rounded-full bg-accent/15 border border-accent/30 items-center justify-center mt-0.5">
          <span className="i-ph:sparkle text-accent text-[13px]" />
        </div>
        <div className="overflow-hidden w-full flex-1 text-secondary text-[15px] leading-[1.62]">
          {/* Metadata row — usage + context */}
          {(codeContext || chatSummary) && (
            <div className="flex items-center gap-2 mb-2">
              {(codeContext || chatSummary) && (
                <Popover
                  side="right"
                  align="start"
                  trigger={
                    <div className="i-ph:info text-muted hover:text-secondary transition-colors cursor-pointer" />
                  }
                >
                  {chatSummary && (
                    <div className="max-w-[min(var(--chat-max-width),calc(100vw-24px))]">
                      <div className="summary max-h-96 flex flex-col">
                        <h2 className="border border-hairline rounded-md p-4">Summary</h2>
                        <div style={{ zoom: 0.7 }} className="overflow-y-auto m-4">
                          <Markdown>{chatSummary}</Markdown>
                        </div>
                      </div>
                      {codeContext && (
                        <div className="code-context flex flex-col p-4 border border-hairline rounded-md">
                          <h2>Context</h2>
                          <div className="flex gap-4 mt-4 dunea" style={{ zoom: 0.6 }}>
                            {codeContext.map((x) => {
                              const normalized = normalizedFilePath(x);
                              return (
                                <Fragment key={normalized}>
                                  <code
                                    className="bg-sunken text-secondary px-1.5 py-1 rounded-md text-accent hover:underline cursor-pointer"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      openFileInWorkbench(normalized);
                                    }}
                                  >
                                    {normalized}
                                  </code>
                                </Fragment>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="context"></div>
                </Popover>
              )}
            </div>
          )}
          {memorySignals.recall && !hideMemoryRecall && <MemoryRecallCard recall={memorySignals.recall} />}

          {subAgentState && <SubAgentProgress state={subAgentState} />}

          {orphanAgentLogs.map(([id, log]) => (
            <StandaloneAgentLog key={id} log={log} />
          ))}

          {/* The plan checklist renders ONCE, pinned above the chat input
              (BaseChat) — not inline per-message, which duplicated it. */}

          {testResultsState && <TestResults state={testResultsState} />}

          {/* Text, reasoning, and tool calls rendered in their original order.
              During an unanswered ask_user turn the trailing text is hidden (the
              question UI shows instead), but prior tools/reasoning still render. */}
          <OrderedSegments
            segments={hasAskUserQuestions ? visibleSegments.filter((s) => s.kind !== 'text') : visibleSegments}
            subAgentToolLogs={subAgentToolLogs}
            markdown={{ append, chatMode, setChatMode, model, provider }}
            // isStreaming is chat-wide; only the message being built is live.
            isStreaming={isStreaming && isLastMessage}
          />

          {hasAskUserQuestions && !isAskUserSubmitted && (
            <AskUserQuestions
              questions={askUserAnnotation.questions}
              onSubmit={handleAskUserSubmit}
              isSubmitted={false}
            />
          )}

          {/* Hover action bar */}
          {content && (
            <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover/msg:opacity-100 touch:opacity-100 transition-opacity">
              <button
                onClick={handleCopyContent}
                aria-label="Copy message"
                className="flex items-center justify-center w-6 h-6 rounded-md bg-transparent border-0 cursor-pointer text-muted hover:text-primary hover:bg-sunken transition-colors touch:h-9 touch:w-9"
              >
                <span className={copied ? 'i-ph:check text-xs' : 'i-ph:copy text-xs'} />
              </button>
              {onRestore && messageId && hasCheckpoint && !isStreaming && (
                <RevertButton messageId={messageId} onRestore={onRestore} />
              )}
              {onFork && messageId && (
                <WithTooltip tooltip="Fork chat from here">
                  <button
                    onClick={() => onFork(messageId)}
                    aria-label="Fork chat from here"
                    className="flex items-center justify-center w-6 h-6 rounded-md bg-transparent border-0 cursor-pointer text-muted hover:text-primary hover:bg-sunken transition-colors touch:h-9 touch:w-9"
                  >
                    <span className="i-ph:git-fork text-xs" />
                  </button>
                </WithTooltip>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
  assistantMessagePropsEqual,
);

/** Hover-bar "Revert to this message" button — confirms before restoring. */
function RevertButton({ messageId, onRestore }: { messageId: string; onRestore: (messageId: string) => void }) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <WithTooltip tooltip="Revert to this message">
        <button
          onClick={() => setConfirmOpen(true)}
          aria-label="Revert to this message"
          className="flex items-center justify-center w-6 h-6 rounded-md bg-transparent border-0 cursor-pointer text-muted hover:text-primary hover:bg-sunken transition-colors touch:h-9 touch:w-9"
        >
          <span className="i-ph:arrow-u-up-left text-xs" />
        </button>
      </WithTooltip>
      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onRestore(messageId);
        }}
        title="Revert to this version?"
        description="This restores your project files to how they were at this point in the conversation. Anything built after this point will be lost from the working copy."
        confirmLabel="Revert"
        variant="destructive"
      />
    </>
  );
}

/** Compact card for a server-appended restore-checkpoint message — replaces the normal bubble. */
function RestoreCard({
  content,
  messageId,
  hasCheckpoint,
  isStreaming,
  onRestore,
}: {
  content: string;
  messageId?: string;
  hasCheckpoint?: boolean;
  isStreaming?: boolean;
  onRestore?: (messageId: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="ui-banner-info">
        <span className="i-ph:clock-counter-clockwise text-accent text-[13px] flex-shrink-0" />
        <span>{content || 'Restored to an earlier version'}</span>
      </div>
      {onRestore && messageId && hasCheckpoint && !isStreaming && (
        <RevertButton messageId={messageId} onRestore={onRestore} />
      )}
    </div>
  );
}

function OrderedSegments({
  segments,
  subAgentToolLogs,
  markdown,
  isStreaming,
}: {
  segments: RenderSegment[];
  subAgentToolLogs: Map<string, { label: string; steps: string[]; streamedText: string; status: string }>;
  markdown: {
    append?: (message: UIMessage) => void;
    chatMode?: 'discuss' | 'build';
    setChatMode?: (mode: 'discuss' | 'build') => void;
    model?: string;
    provider?: ProviderInfo;
  };
  isStreaming?: boolean;
}) {
  const blocks = foldTurn(foldSegments(segments), isStreaming);

  // A step inside the folded "worked" line: plain rows, one-line narration.
  const renderStep = (block: Block) => {
    switch (block.kind) {
      case 'reasoning':
        return <ThinkingBlock key={block.id} reasoning={block.text} />;
      case 'sub-agent': {
        const tcId = (block.part.output as any)?.toolCallId as string | undefined;
        const agentLog = (tcId && subAgentToolLogs.get(tcId)) || subAgentToolLogs.get(block.part.toolCallId);

        return <SubAgentGroup key={block.id} part={block.part} agentLog={agentLog} isStreaming={isStreaming} />;
      }
      case 'file':
        return <ToolLine key={block.id} part={block.part} isStreaming={isStreaming} />;
      case 'tool-group':
        return block.parts.length === 1 ? (
          <ToolLine key={block.id} part={block.parts[0]} isStreaming={isStreaming} />
        ) : (
          <ToolGroup key={block.id} parts={block.parts} isStreaming={isStreaming} />
        );
      default: {
        const lines = block.text
          .split(/\n{2,}/)
          .map(narrationLine)
          .filter(Boolean);

        return lines.length > 0 ? (
          <div key={block.id}>
            {lines.map((line, i) => (
              <div key={i} className="py-[3px] text-[13px] leading-[1.5] text-faint">
                {line}
              </div>
            ))}
          </div>
        ) : null;
      }
    }
  };

  return (
    <>
      {blocks.map((block) => {
        if (block.kind === 'worked') {
          return (
            <WorkedBlock
              key={block.id}
              blocks={block.blocks}
              parts={block.parts}
              isStreaming={isStreaming}
              renderBlock={renderStep}
            />
          );
        }

        if (block.kind !== 'text') {
          return renderStep(block);
        }

        return (
          <div key={block.id} className="leading-[1.65] my-2 first:mt-0 last:mb-0">
            <Markdown
              append={markdown.append}
              chatMode={markdown.chatMode}
              setChatMode={markdown.setChatMode}
              model={markdown.model}
              provider={markdown.provider}
              html
            >
              {block.text}
            </Markdown>
          </div>
        );
      })}
    </>
  );
}

/**
 * A sub-agent progress log with no tool part to attach to — a server-side
 * pre-pass (the clone source capture). Shows the label, the latest steps, and
 * the completion summary, styled like the other agent panels.
 */
function StandaloneAgentLog({ log }: { log: { label: string; steps: string[]; status: string; summary?: string } }) {
  const MAX_VISIBLE_STEPS = 6;
  const steps = log.steps.slice(-MAX_VISIBLE_STEPS);
  const running = log.status === 'running';
  const failed = log.status === 'error';

  // Same shape as the build timeline: a header row, then the latest steps on a rail.
  return (
    <div className="mb-2.5">
      <div className="flex items-center gap-2 py-1">
        {running ? (
          <DuneaSpinner size={14} className="text-accent flex-shrink-0" />
        ) : (
          <span
            className={`${failed ? 'i-ph:warning-circle text-danger' : 'i-ph:check-circle text-accent/80'} text-[15px] flex-shrink-0`}
          />
        )}
        <span className={`text-[14px] font-medium truncate ${running ? 'text-primary' : 'text-secondary'}`}>
          {log.label}
        </span>
      </div>
      {(steps.length > 0 || log.summary) && (
        <div className="mt-1.5 rounded-xl border border-hairline bg-surface/40 px-3.5 py-2.5">
          {steps.map((step, i) => (
            <div
              key={`${i}-${step.slice(0, 24)}`}
              className={`py-[3px] text-[13px] leading-[1.45] ${running && i === steps.length - 1 ? 'text-secondary' : 'text-faint'}`}
            >
              {step}
            </div>
          ))}
          {log.summary && <div className="pt-1 text-[13px] leading-[1.45] text-muted">{log.summary}</div>}
        </div>
      )}
    </div>
  );
}

function ThinkingBlock({ reasoning }: { reasoning: string }) {
  const [isOpen, setIsOpen] = useState(false);

  // A borderless one-line "Thought" (as in Dunea Work), closed by default.
  return (
    <div className="my-px">
      <div
        className="group/th flex items-center gap-2 py-[3px] px-1.5 -mx-1.5 rounded-md cursor-pointer select-none transition-colors hover:bg-white/[0.025]"
        onClick={() => setIsOpen(!isOpen)}
        title={isOpen ? 'Hide the thinking' : 'See how Dunea thought about this step'}
      >
        <span className="i-ph:lightbulb text-[13px] text-faint flex-shrink-0" />
        <span className="text-[13.5px] text-muted">Thought</span>
        <span
          className={`i-ph:caret-right text-[11px] text-faint flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-90' : 'opacity-0 group-hover/th:opacity-100 touch:opacity-100'
          }`}
        />
      </div>
      {isOpen && (
        <div className="ml-[6px] pl-3.5 border-l border-hairline my-1 max-h-72 overflow-y-auto modern-scrollbar">
          <p className="text-[13px] italic text-muted whitespace-pre-wrap leading-[1.6]">
            {reasoning
              .replace(/\*\*|`/g, '')
              .replace(/\n{3,}/g, '\n\n')
              .trim()}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Recall card — the "what I remembered before starting" event. Deliberately
 * mirrors ThinkingBlock's card (same container width, dashed border, radius,
 * icon column at px-3/gap-2, and vertical spacing) so memory reads as another
 * sibling event in the timeline rather than a floating row. Only the icon
 * (brain), title, and body differ. Not collapsible — the body is a single line.
 */
function MemoryRecallCard({ recall }: { recall: MemoryRecallAnnotation }) {
  // A slim row in the build timeline's style (round icon node, plain sentence),
  // not a boxed card with a shouted label.
  return (
    <WithTooltip
      tooltip="What Dunea learned in earlier chats — your preferences and this project's details — so it doesn't ask again. Manage it in Account → Memory."
      position="top"
      delay={300}
      maxWidth={300}
    >
      <div className="mb-2.5 inline-flex items-center gap-2.5 rounded-full border border-hairline bg-surface/40 py-1 pl-1 pr-3 cursor-default">
        <span className="flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center rounded-full border border-hairline bg-surface">
          <span className="i-ph:brain text-[11px] text-accent" />
        </span>
        <span className="text-[13px] text-muted">Remembered {recallSummary(recall)} from past sessions</span>
      </div>
    </WithTooltip>
  );
}

// The end-of-turn "Remembered <keys>" chip row was intentionally removed from
// the UI (too technical for the chat timeline — internal memory keys read as
// machinery, not product). Memory still saves exactly as before and remains
// fully visible/manageable in Account → Memory.
